// 미저장 이탈 경고(표 dev.unsavedGuard, 2026-10-02 LUA — LES 공통, MOMS 는 나중에).
//
// 왜: LES(센터 관리·스토어 관리)는 셀을 고치면 바로 저장되던 MWMS 방식 대신 **[저장]을 눌러야 저장**한다(MOMS 와 같은 문법).
//   그러면 고쳐 놓고 저장 안 한 채 떠나는 일이 생긴다 → 저장 안 된 값이 있으면 떠나기 전에 묻는다.
//
// 계약(데모):
//  · 표(dev.unsavedGuard + rowKey)가 '저장 안 된 행 수'를 여기에 올린다. 기준 = 마지막 저장본(처음 렌더·[저장] 확정 시점).
//    값을 원래대로 되돌리면 미저장에서 빠진다. 새로 들어온 행(추가 모달 저장)은 저장된 것으로 본다.
//  · [저장] = 확인창 okAction 'commitTableRows' → 그 표의 **체크한 행**을 저장본으로 확정(체크 안 한 수정분은 남는다).
//  · 떠남 = 왼쪽 메뉴 이동(App.guardNavigate) · [조회]/[초기화](같은 화면이지만 수정분이 날아간다 — LUA 확정) → 확인창.
//    [이동]/[계속] 하면 수정분을 저장본으로 되돌리고 진행. 새로고침·창 닫기 = 브라우저 기본 확인(beforeunload).
//  · 화면 단위 — 표가 언마운트되면 자기 항목을 지운다(다른 화면으로 새지 않게).
import { core } from '@musinsa/pbo-core'

const unsaved = new Map<string, number>()
const reverters = new Map<string, () => void>()

/** 표가 자기 미저장 행 수를 올린다(0 이면 항목 삭제). revert = 수정분을 저장본으로 되돌리는 함수. */
export function setTableUnsaved(tableId: string, count: number, revert: () => void) {
  if (count > 0) unsaved.set(tableId, count)
  else unsaved.delete(tableId)
  reverters.set(tableId, revert)
}

/** 표 언마운트 — 자기 항목 정리. */
export function clearTableUnsaved(tableId: string) {
  unsaved.delete(tableId)
  reverters.delete(tableId)
}

export function hasUnsavedEdits(): boolean {
  for (const n of unsaved.values()) if (n > 0) return true
  return false
}

/** 미저장 수정분을 전부 저장본으로 되돌린다. */
export function revertUnsavedEdits() {
  for (const id of Array.from(unsaved.keys())) reverters.get(id)?.()
  unsaved.clear()
}

/** 문구 — 편집기 이탈 경고('저장하지 않은 변경사항이 있습니다')와 같은 말투. */
export const UNSAVED_TITLE = '저장하지 않은 변경사항이 있습니다'
export const UNSAVED_CONTENT = '페이지를 벗어나면 수정한 내용이 사라집니다. 이동할까요?'
export const UNSAVED_CONTENT_REFRESH = '다시 조회하면 수정한 내용이 사라집니다. 계속할까요?'

/**
 * 진행 전 확인 — 미저장이 없으면 바로 proceed. 있으면 확인창, [확인] 시 수정분을 되돌리고 proceed.
 *  kind 'leave' = 화면 이동(버튼 [이동]) / 'refresh' = [조회]·[초기화](버튼 [계속]).
 */
export function confirmDiscardUnsaved(proceed: () => void, kind: 'leave' | 'refresh' = 'leave') {
  if (!hasUnsavedEdits()) {
    proceed()
    return
  }
  core.Modal.confirm({
    centered: true,
    title: UNSAVED_TITLE,
    content: kind === 'leave' ? UNSAVED_CONTENT : UNSAVED_CONTENT_REFRESH,
    okText: kind === 'leave' ? '이동' : '계속',
    cancelText: '취소',
    okButtonProps: { danger: true },
    onOk: () => {
      revertUnsavedEdits()
      proceed()
    },
  })
}

// 새로고침·창 닫기 — 브라우저 기본 확인(문구는 브라우저가 정한다). 모듈 로드 시 한 번 건다.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (!hasUnsavedEdits()) return
    e.preventDefault()
    e.returnValue = ''
  })
}
